import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Button,
    ButtonBase,
    IconButton,
    Popover,
    TextField,
    Typography
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

function getPrecision(step) {
    const stepString = step.toString();
    if (stepString.includes("e-")) {
        const [, exponent] = stepString.split("e-");
        return parseInt(exponent, 10);
    }
    const decimalIndex = stepString.indexOf(".");
    if (decimalIndex === -1) {
        return 0;
    }
    return stepString.length - decimalIndex - 1;
}

function formatFromInt(value, precision, decimals) {
    if (value === null || value === undefined) {
        return "";
    }
    const numeric = value / precision;
    const fixed = numeric.toFixed(decimals);
    const numericFixed = parseFloat(fixed);
    if (Math.abs(numericFixed) < Number.EPSILON) {
        return Number.parseFloat(fixed).toFixed(decimals);
    }
    return numericFixed > 0 ? `+${fixed}` : fixed;
}

function parseDisplayValue(displayValue) {
    if (displayValue === null || displayValue === undefined || displayValue === "") {
        return null;
    }
    const cleaned = displayValue.replace("+", "");
    const parsed = Number.parseFloat(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
}

function buildSignedPairs(min, max, step, precision) {
    const intStep = Math.round(step * precision);
    const intMin = Math.round(min * precision);
    const intMax = Math.round(max * precision);

    const negatives = [];
    for (let current = -intStep; current >= intMin; current -= intStep) {
        negatives.push(current);
    }

    const positives = [];
    for (let current = intStep; current <= intMax; current += intStep) {
        positives.push(current);
    }

    const length = Math.max(negatives.length, positives.length);
    const pairs = [];
    for (let index = 0; index < length; index += 1) {
        pairs.push({
            negative: negatives[index] ?? null,
            positive: positives[index] ?? null
        });
    }
    return pairs;
}

function ValueCell({ label, onSelect, selected }) {
    if (label === null) {
        return <Box />;
    }

    return (
        <ButtonBase
            onClick={onSelect}
            sx={{
                borderRadius: 0.5,
                p: 0.75,
                fontSize: "0.875rem",
                justifyContent: "center",
                display: "flex",
                width: "100%",
                bgcolor: selected ? "primary.main" : "transparent",
                color: selected ? "primary.contrastText" : "text.primary",
                "&:hover": {
                    bgcolor: selected ? "primary.main" : "action.hover"
                }
            }}
        >
            {label}
        </ButtonBase>
    );
}

ValueCell.propTypes = {
    label: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    selected: PropTypes.bool
};

ValueCell.defaultProps = {
    label: null,
    selected: false
};

export default function SignedValueSelector({
    name,
    value,
    onChange,
    min,
    max,
    step,
    decimals,
    placeholder,
    allowClear,
    textFieldSx
}) {
    const resolvedDecimals = decimals ?? Math.max(2, getPrecision(step));
    const precision = useMemo(() => 10 ** resolvedDecimals, [resolvedDecimals]);
    const [anchorEl, setAnchorEl] = useState(null);

    const bounds = useMemo(() => {
        const intStep = Math.round(step * precision);
        return {
            intStep,
            intMin: Math.round(min * precision),
            intMax: Math.round(max * precision)
        };
    }, [min, max, step, precision]);

    const numericValue = useMemo(() => parseDisplayValue(value), [value]);
    const intValue = useMemo(() => {
        if (numericValue === null) {
            return null;
        }
        return Math.round(numericValue * precision);
    }, [numericValue, precision]);

    const displayValue = useMemo(() => {
        if (intValue === null) {
            return "";
        }
        return formatFromInt(intValue, precision, resolvedDecimals);
    }, [intValue, precision, resolvedDecimals]);

    const pairs = useMemo(
        () => buildSignedPairs(min, max, step, precision),
        [min, max, step, precision]
    );

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const commitValue = (intAmount, { closeMenu = true } = {}) => {
        if (intAmount === null) {
            onChange(name, "");
        } else {
            const formatted = formatFromInt(intAmount, precision, resolvedDecimals);
            onChange(name, formatted);
        }
        if (closeMenu) {
            handleClose();
        }
    };

    const adjustValue = (direction) => {
        const current = intValue ?? 0;
        const next = Math.min(
            bounds.intMax,
            Math.max(bounds.intMin, current + direction * bounds.intStep)
        );
        commitValue(next, { closeMenu: false });
    };

    const open = Boolean(anchorEl);
    const atMin = intValue !== null && intValue <= bounds.intMin;
    const atMax = intValue !== null && intValue >= bounds.intMax;

    return (
        <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
            <IconButton
                size="small"
                onClick={() => adjustValue(-1)}
                aria-label={`Decrease ${name}`}
                sx={{ p: 0.5 }}
                disabled={atMin}
            >
                <RemoveIcon fontSize="small" />
            </IconButton>
            <TextField
                value={displayValue}
                placeholder={placeholder}
                size="small"
                onClick={handleOpen}
                InputProps={{
                    readOnly: true,
                    endAdornment: <ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />,
                    sx: { cursor: "pointer" }
                }}
                sx={{ width: 96, ...textFieldSx }}
            />
            <IconButton
                size="small"
                onClick={() => adjustValue(1)}
                aria-label={`Increase ${name}`}
                sx={{ p: 0.5 }}
                disabled={atMax}
            >
                <AddIcon fontSize="small" />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
                transformOrigin={{ horizontal: "center", vertical: "top" }}
                PaperProps={{ sx: { p: 1, maxHeight: 320, overflowY: "auto" } }}
            >
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(72px, 1fr))" gap={0.5}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => adjustValue(-1)}
                        disabled={atMin}
                        sx={{ gridColumn: "span 1" }}
                    >
                        -
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => adjustValue(1)}
                        disabled={atMax}
                        sx={{ gridColumn: "span 1" }}
                    >
                        +
                    </Button>
                    <Button
                        variant={intValue === 0 ? "contained" : "text"}
                        size="small"
                        onClick={() => commitValue(0)}
                        sx={{ gridColumn: "span 2", justifyContent: "center" }}
                    >
                        0.00
                    </Button>
                    {pairs.map((pair, index) => {
                        const negativeLabel = pair.negative !== null
                            ? formatFromInt(pair.negative, precision, resolvedDecimals)
                            : null;
                        const positiveLabel = pair.positive !== null
                            ? formatFromInt(pair.positive, precision, resolvedDecimals)
                            : null;
                        return (
                            <Box
                                key={`pair-${index}`}
                                display="contents"
                            >
                                <ValueCell
                                    label={negativeLabel}
                                    onSelect={() => commitValue(pair.negative)}
                                    selected={pair.negative !== null && pair.negative === intValue}
                                />
                                <ValueCell
                                    label={positiveLabel}
                                    onSelect={() => commitValue(pair.positive)}
                                    selected={pair.positive !== null && pair.positive === intValue}
                                />
                            </Box>
                        );
                    })}
                </Box>
                {allowClear && (
                    <Box mt={1} display="flex" justifyContent="center">
                        <Button
                            size="small"
                            startIcon={<ClearIcon fontSize="small" />}
                            onClick={() => commitValue(null)}
                        >
                            Clear
                        </Button>
                    </Box>
                )}
                {!allowClear && (
                    <Box mt={1} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Negative values appear on the left, positive on the right.
                </Typography>
            </Popover>
        </Box>
    );
}

SignedValueSelector.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
    decimals: PropTypes.number,
    placeholder: PropTypes.string,
    allowClear: PropTypes.bool,
    textFieldSx: PropTypes.object
};

SignedValueSelector.defaultProps = {
    value: "",
    decimals: undefined,
    placeholder: "-",
    allowClear: true,
    textFieldSx: undefined
};
